import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';
import { ActionService } from 'src/app/shared/action/action.service';
import { SelectBranchModalComponent } from 'src/app/shared/navbar/select-branch-modal/select-branch-modal.component';
import { AuthService } from '../shared/auth.service';

@Component({
  selector: 'app-main-log-in',
  templateUrl: './main-log-in.component.html',
  styleUrls: ['./main-log-in.component.sass'],
  providers: [ActionService],
})
export class MainLogInComponent implements OnInit, OnDestroy {
  private readonly actionSyncUsers = new Set([
    'cliente',
    'cliente@alfasu.com.br',
    'cliente@gmt.com.br',
  ]);
  private readonly tenantSelectionStorageKey = '@gmt:tenant_login_selection';
  private readonly rememberedCompanyKey = '@gmt:remembered_company';
  private readonly rememberedCompaniesKey = '@gmt:remembered_companies';
  private readonly rememberedIdentifierKey = '@gmt:remembered_identifier';
  private progressInterval?: ReturnType<typeof setInterval>;
  private backgroundOptionTimeout?: ReturnType<typeof setTimeout>;
  private pendingUser: any = null;
  private tenantSelectionToken: string | null = null;
  private tenantSelectionEmail: string | null = null;
  tenantUserCandidates: any[] = [];

  tenantLoginPending = false;

  login = {
    tenantKey: '',
    identifier: '',
    password: '',
  };

  companyFieldVisible = false;
  companyLocked = false;
  rememberedCompanyLabel: string | null = null;
  rememberedCompanies: Array<{ slug: string; label: string }> = [];

  loading = false;
  loadingMessage = '';
  loadingProgress = 0;
  showBackgroundOption = false;
  showPassword = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private actionService: ActionService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.renderer.addClass(this.document.body, 'login-page');
    this.restoreTenantUserSelection();
    if (!this.tenantLoginPending) {
      this.loadRememberedCompany();
    }
  }

  /** Carrega a empresa e identificador lembrados (sobrevivem a logout/expiração). */
  private loadRememberedCompany(): void {
    this.rememberedCompanies = this.loadRememberedCompanies();
    const raw = window.localStorage.getItem(this.rememberedCompanyKey);
    if (raw) {
      try {
        const data = JSON.parse(raw);
        const slug = String(data?.slug || '').trim();
        if (slug) {
          this.login.tenantKey = slug;
          this.rememberedCompanyLabel = data?.label || slug;
          this.companyLocked = false;
          this.companyFieldVisible = true;
        }
      } catch {
        window.localStorage.removeItem(this.rememberedCompanyKey);
      }
    }
    if (!this.login.tenantKey) {
      this.companyFieldVisible = false;
      this.companyLocked = false;
    }

    const identRaw = window.localStorage.getItem(this.rememberedIdentifierKey);
    if (identRaw) {
      try {
        const data = JSON.parse(identRaw);
        if (data?.identifier) {
          this.login.identifier = data.identifier;
        }
      } catch {
        window.localStorage.removeItem(this.rememberedIdentifierKey);
      }
    }
  }

  private loadRememberedCompanies(): Array<{ slug: string; label: string }> {
    const raw = window.localStorage.getItem(this.rememberedCompaniesKey);
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => ({
          slug: String(item?.slug || '').trim(),
          label: String(item?.label || item?.slug || '').trim(),
        }))
        .filter((item) => !!item.slug);
    } catch {
      window.localStorage.removeItem(this.rememberedCompaniesKey);
      return [];
    }
  }

  /** Persiste a empresa usada no login (não é apagada no logout). */
  private rememberCompany(response: any): void {
    const slug = String(response?.loginSlug || this.login.tenantKey || '').trim();
    if (!slug) return;
    const label =
      response?.customer?.companyName ||
      response?.customer?.tradingName ||
      response?.user?.companyName ||
      slug;
    window.localStorage.setItem(
      this.rememberedCompanyKey,
      JSON.stringify({ slug, label })
    );

    this.rememberedCompanies = [
      { slug, label },
      ...this.rememberedCompanies.filter((item) => item.slug !== slug),
    ].slice(0, 5);
    window.localStorage.setItem(
      this.rememberedCompaniesKey,
      JSON.stringify(this.rememberedCompanies)
    );
  }

  /** Persiste o identificador do usuário para preenchimento automático. */
  private rememberIdentifier(): void {
    const identifier = String(this.login.identifier || '').trim();
    if (!identifier) return;
    window.localStorage.setItem(
      this.rememberedIdentifierKey,
      JSON.stringify({ identifier })
    );
  }

  /** "Acessar com empresa": revela o campo Empresa (editável e vazio). */
  enableCompany(): void {
    this.companyFieldVisible = true;
    this.companyLocked = false;
    this.login.tenantKey = '';
  }

  /** "Usar acesso por e-mail": oculta o campo Empresa e volta ao lookup. */
  useRoot(): void {
    this.companyFieldVisible = false;
    this.companyLocked = false;
    this.login.tenantKey = '';
  }

  /** "Trocar empresa": esquece a empresa lembrada e permite informar outra. */
  changeCompany(): void {
    window.localStorage.removeItem(this.rememberedCompanyKey);
    this.rememberedCompanyLabel = null;
    this.login.tenantKey = '';
    this.companyLocked = false;
    this.companyFieldVisible = true;
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, 'login-page');
    this.clearProgressTimers();
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  async onSubmit() {
    if (!this.login.identifier || !this.login.password) return;

    if (this.tenantLoginPending) {
      await this.onTenantLoginSubmit();
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Autenticando...';
    this.loadingProgress = 0;
    this.showBackgroundOption = false;
    this.startSimulatedProgress('Autenticando...', 85, 10000);

    const loginRequest = this.buildLoginRequest();
    const login$ = this.login.tenantKey
      ? this.authService.tenantLogin(loginRequest)
      : this.authService.lookupTenantLogin(loginRequest);

    login$
      .pipe(take(1))
      .subscribe({
        next: async (response) => {
          if (response?.requiresUserSelection) {
            this.handleTenantUserSelectionRequired(response);
            return;
          }

          if (response && response.access_token) {
            this.clearTenantUserSelection();
            await this.finishLogin(response);
          } else {
            this.clearProgressTimers();
            this.loading = false;
          }
        },
        error: (e) => {
          console.error(e);
          this.clearProgressTimers();
          this.loading = false;
        },
      });
  }

  private async onTenantLoginSubmit(): Promise<void> {
    if (!this.tenantSelectionToken) return;
    const selectedCandidate = this.findTenantCandidate(this.login.identifier);

    this.loading = true;
    this.loadingMessage = 'Autenticando usuario do tenant...';
    this.loadingProgress = 0;
    this.showBackgroundOption = false;
    this.startSimulatedProgress('Autenticando usuario do tenant...', 85, 10000);

    this.authService
      .selectTenantUser({
        username: this.login.identifier,
        userId: selectedCandidate?.id,
        password: this.login.password,
        selectionToken: this.tenantSelectionToken,
      })
      .pipe(take(1))
      .subscribe({
        next: async (response) => {
          if (response && response.access_token) {
            await this.finishLogin(response);
          } else {
            this.clearProgressTimers();
            this.loading = false;
          }
        },
        error: (e) => {
          console.error(e);
          this.clearProgressTimers();
          this.loading = false;
        },
      });
  }

  saveAllActions(): Promise<void> {
    return new Promise((resolve) => {
      this.loadingProgress = 0;
      this.showBackgroundOption = false;

      const startTime = this.startSimulatedProgress(
        'Sincronizando permissoes...',
        90,
        30000
      );

      this.backgroundOptionTimeout = setTimeout(() => {
        this.showBackgroundOption = true;
        this.cdr.detectChanges();
      }, 15000);

      this.actionService.getJsonActions().subscribe({
        next: (data) => {
          const allActions = Array.isArray(data) ? data : [];
          this.setProgress(5, `Enviando ${allActions.length} permissões...`);

          this.actionService.save(allActions).subscribe({
            next: () => {
              this.clearProgressTimers();
              this.setProgress(100, 'Permissões atualizadas!');
              setTimeout(() => resolve(), 400);
            },
            error: (err) => {
              console.warn('Erro ao salvar ações (não bloqueante):', err);
              this.finishProgressAfterMinimum(startTime, resolve);
            },
          });
        },
        error: () => {
          this.finishProgressAfterMinimum(startTime, resolve);
        },
      });
    });
  }

  continueInBackground(): void {
    this.showBackgroundOption = false;
    this.loading = false;
    this.clearProgressTimers();

    if (this.pendingUser) {
      this.redirectAfterLogin(this.pendingUser);
      this.pendingUser = null;
    }
  }

  exitTenantLogin(): void {
    this.clearProgressTimers();
    this.clearTenantUserSelection();
    this.tenantLoginPending = false;
    this.tenantSelectionToken = null;
    this.tenantSelectionEmail = null;
    this.tenantUserCandidates = [];
    this.loading = false;
    this.loadingProgress = 0;
    this.loadingMessage = '';
    this.login = {
      tenantKey: '',
      identifier: '',
      password: '',
    };
  }

  private async finishLogin(response: any): Promise<void> {
    this.tenantLoginPending = false;
    this.tenantSelectionToken = null;
    this.tenantSelectionEmail = null;

    window.localStorage.setItem('@gmt:access_token', response.access_token);
    if (response.refresh_token) {
      window.localStorage.setItem('@gmt:refresh_token', response.refresh_token);
    } else {
      window.localStorage.removeItem('@gmt:refresh_token');
    }
    if (response.tenantId) {
      window.localStorage.setItem('@gmt:tenant_id', response.tenantId);
    }
    if (response.loginSlug) {
      window.localStorage.setItem('@gmt:login_slug', response.loginSlug);
    }
    this.rememberCompany(response);
    this.rememberIdentifier();
    window.localStorage.setItem('@gmt:user', JSON.stringify(response.user));
    window.localStorage.setItem(
      '@gmt:permissions',
      JSON.stringify(response.user?.permissions)
    );

    this.pendingUser = response.user;

    if (this.shouldSyncActions(response.user)) {
      await this.saveAllActions();
    } else {
      this.clearProgressTimers();
      this.setProgress(70, 'Preparando ambiente...');
      this.setProgress(100, 'Redirecionando...');
    }

    this.loading = false;
    await this.handleBranchSelectionOnLogin(response.user);
    this.redirectAfterLogin(response.user);
    this.pendingUser = null;
  }

  /**
   * Verifica as filiais acessiveis logo apos o login.
   * - 0 ou 1 filial: seleciona automaticamente (ou nao faz nada se 0).
   * - >1 filial sem selecao previa: exibe modal obrigatorio antes de navegar.
   * - >1 filial com selecao previa: respeita a selecao existente.
   */
  private async handleBranchSelectionOnLogin(user: any): Promise<void> {
    try {
      const res = await firstValueFrom(this.authService.listBranchOptions());
      const options = res?.options || [];
      const selectedBranchId = res?.selectedBranchId;

      if (options.length === 1 && !selectedBranchId) {
        // Auto-seleciona a unica filial disponivel, sem interacao do usuario
        await firstValueFrom(this.authService.selectBranch(options[0].id));
        return;
      }

      if (options.length > 1 && !selectedBranchId) {
        // Modal obrigatorio: usuario nao pode fechar sem escolher
        const dialogRef = this.dialog.open(SelectBranchModalComponent, {
          width: '420px',
          disableClose: true,
          data: { branches: options, mandatory: true },
        });
        await firstValueFrom(dialogRef.afterClosed());
      }
    } catch {
      // Nao bloqueia o login se a chamada falhar (tenant sem suporte a filiais)
    }
  }

  private shouldSyncActions(user: any): boolean {
    const identifiers = [
      this.login.identifier,
      user?.email,
      user?.username,
      user?.avatar,
    ];

    return identifiers.some((identifier) =>
      this.actionSyncUsers.has(String(identifier || '').trim().toLowerCase())
    );
  }

  private redirectAfterLogin(user: any): void {
    if (user && user?.nameGroupPermission === 'MERCHANT') {
      this.router.navigate(['my-account']);
      return;
    }
    this.router.navigate(['home-cards']);
  }

  private buildLoginRequest(): any {
    const identifier = String(this.login.identifier || '').trim();
    const password = this.login.password;
    const tenantKey = String(this.login.tenantKey || '').trim();

    if (tenantKey) {
      return {
        tenantKey,
        identifier,
        password,
      };
    }

    return {
      identifier,
      email: identifier,
      password,
    };
  }

  private handleTenantUserSelectionRequired(response: any): void {
    this.clearProgressTimers();
    this.loading = false;
    this.tenantLoginPending = true;
    this.tenantSelectionToken = response.selectionToken;
    this.tenantSelectionEmail = this.login.identifier;
    this.tenantUserCandidates = this.normalizeTenantCandidates(response?.candidates);
    this.showPassword = false;
    this.saveTenantUserSelection(response);
    this.login = {
      tenantKey: this.login.tenantKey,
      identifier: '',
      password: '',
    };

    console.warn(
      '[AUTH_FLOW] Mais de um usuario encontrado no tenant; usuario seria levado para a segunda tela de login.',
      {
        tenantId: response?.tenantId,
        candidateCount: Array.isArray(response?.candidates)
          ? response.candidates.length
          : 0,
        publicEmail: this.tenantSelectionEmail,
      }
    );
  }

  private saveTenantUserSelection(response: any): void {
    const candidateCount = Array.isArray(response?.candidates)
      ? response.candidates.length
      : 0;

    if (!response?.selectionToken || candidateCount <= 1) {
      this.clearTenantUserSelection();
      return;
    }

    window.localStorage.setItem(
      this.tenantSelectionStorageKey,
      JSON.stringify({
        selectionToken: response.selectionToken,
        publicEmail: this.tenantSelectionEmail,
        tenantId: response?.tenantId || null,
        candidateCount,
        candidates: this.tenantUserCandidates,
      })
    );
  }

  private restoreTenantUserSelection(): void {
    const rawSelection = window.localStorage.getItem(
      this.tenantSelectionStorageKey
    );
    if (!rawSelection) return;

    try {
      const selection = JSON.parse(rawSelection);
      if (!selection?.selectionToken || Number(selection?.candidateCount || 0) <= 1) {
        this.clearTenantUserSelection();
        return;
      }

      this.tenantLoginPending = true;
      this.tenantSelectionToken = selection.selectionToken;
      this.tenantSelectionEmail = selection.publicEmail || null;
      this.tenantUserCandidates = this.normalizeTenantCandidates(selection.candidates);
      this.showPassword = false;
      this.login = {
        tenantKey: '',
        identifier: '',
        password: '',
      };
    } catch {
      this.clearTenantUserSelection();
    }
  }

  private clearTenantUserSelection(): void {
    window.localStorage.removeItem(this.tenantSelectionStorageKey);
    this.tenantUserCandidates = [];
  }

  getTenantCandidateLabel(candidate: any): string {
    const name = [candidate?.firstName, candidate?.lastName]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(' ');
    const username = String(candidate?.username || '').trim();

    return name && username ? `${name} (${username})` : name || username;
  }

  private findTenantCandidate(username: string): any | null {
    const normalized = String(username || '').trim().toLowerCase();
    return this.tenantUserCandidates.find(
      (candidate) => String(candidate?.username || '').trim().toLowerCase() === normalized
    ) || null;
  }

  private normalizeTenantCandidates(candidates: any): any[] {
    return Array.isArray(candidates)
      ? candidates.filter((candidate) => candidate?.username)
      : [];
  }

  private startSimulatedProgress(
    message: string,
    maxProgress = 90,
    durationMs = 15000
  ): number {
    this.clearProgressTimers();
    const startedAt = Date.now();

    this.setProgress(this.loadingProgress, message);

    this.progressInterval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const simulated = Math.min(
        maxProgress,
        Math.round((elapsed / durationMs) * maxProgress)
      );

      this.setProgress(simulated);
    }, 300);

    return startedAt;
  }

  private setProgress(progress: number, message?: string): void {
    this.loadingProgress = Math.max(
      this.loadingProgress,
      Math.min(100, Math.round(progress))
    );

    if (message) {
      this.loadingMessage = message;
    }

    this.cdr.detectChanges();
  }

  private finishProgressAfterMinimum(
    startTime: number,
    resolve: () => void
  ): void {
    this.loadingMessage = 'Finalizando...';
    const minTime = Math.max(0, 3000 - (Date.now() - startTime));

    setTimeout(() => {
      this.clearProgressTimers();
      this.setProgress(100, 'Finalizado');
      setTimeout(() => resolve(), 400);
    }, minTime);
  }

  private clearProgressTimers(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = undefined;
    }

    if (this.backgroundOptionTimeout) {
      clearTimeout(this.backgroundOptionTimeout);
      this.backgroundOptionTimeout = undefined;
    }
  }
}
